(() => {
  if (window.LimitlessKitSelectorScriptLoaded) return;
  window.LimitlessKitSelectorScriptLoaded = true;

  const rootSelector = '[data-limitless-kit-selector]';
  const metadataKeys = new Set(['Engine Series', 'CPL #']);
  const productFieldPattern = /(kit|gasket|injector|piston|turbo|cyl|fuel)/i;
  const partNumberPattern = /\b(?:M|R)-[A-Z0-9.-]+|\bIF\/OH\s*[A-Z0-9.-]+/gi;

  const defaultConfig = {
    appProxyBasePath: '/apps/limitless-selectors',
    seriesPlaceholder: 'Select engine series',
    cplPlaceholder: 'Select CPL',
    chooseSeriesText: 'Select an engine series to begin.',
    chooseText: 'Select a CPL to see matching kits.',
    emptyText: 'No matching kit data was found for this selection.',
    productBasePath: '/products/',
    productHandlePrefix: '',
    productHandleSuffix: '',
    productLinkMode: 'search_exact',
    searchBasePath: '/search'
  };

  const escapeHtml = (value) => String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

  const readConfig = (root) => {
    const configScript = root.querySelector('[data-kit-config]');
    if (!configScript) return { ...defaultConfig };

    try {
      return {
        ...defaultConfig,
        ...JSON.parse(configScript.textContent || '{}')
      };
    } catch (error) {
      return { ...defaultConfig };
    }
  };

  const normalizeProxyBase = (base) => String(base || defaultConfig.appProxyBasePath).replace(/\/+$/, '');

  const buildProxyUrl = (base, path, params = {}) => {
    const proxyBase = normalizeProxyBase(base);
    const url = /^https?:\/\//i.test(proxyBase)
      ? new URL(`${proxyBase}${path}`)
      : new URL(`${proxyBase}${path}`, window.location.origin);

    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        url.searchParams.set(key, value);
      }
    });

    return url.toString();
  };

  const fetchJson = async (url) => {
    const response = await fetch(url, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    });

    const payload = await response.json().catch(() => null);

    if (!response.ok || !payload || payload.ok === false) {
      throw new Error(payload?.error || `Selector request failed with status ${response.status}`);
    }

    return payload;
  };

  const setOptions = (select, values, placeholder) => {
    select.innerHTML = '';

    const placeholderOption = document.createElement('option');
    placeholderOption.className = 'quick-filter__select-label';
    placeholderOption.value = '';
    placeholderOption.textContent = placeholder;
    select.appendChild(placeholderOption);

    values.forEach((value) => {
      const option = document.createElement('option');
      option.value = value;
      option.textContent = value;
      select.appendChild(option);
    });

    select.value = '';
  };

  const renderEmpty = (results, text) => {
    results.innerHTML = `<div class="limitless-kit-selector__empty">${escapeHtml(text)}</div>`;
  };

  const handleize = (value) => String(value)
    .trim()
    .toLowerCase()
    .replace(/&/g, 'and')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

  const buildProductHandle = (number, config) => [
    config.productHandlePrefix,
    number,
    config.productHandleSuffix
  ]
    .map(handleize)
    .filter(Boolean)
    .join('-');

  const normalizeProductBase = (base) => {
    const value = String(base || '/products/');
    return value.endsWith('/') ? value : `${value}/`;
  };

  const buildSearchUrl = (number, config) => {
    const searchBase = String(config.searchBasePath || '/search');
    const separator = searchBase.includes('?') ? '&' : '?';
    return `${searchBase}${separator}type=product&options%5Bprefix%5D=last&q=${encodeURIComponent(`"${String(number).trim()}"`)}`;
  };

  const buildProductUrl = (number, config) => {
    if (config.productLinkMode === 'product_handle') {
      const handle = buildProductHandle(number, config);
      return handle ? `${normalizeProductBase(config.productBasePath)}${handle}` : '';
    }

    return buildSearchUrl(number, config);
  };

  const unique = (items) => [...new Set(items.filter(Boolean))];

  const expandProductNumber = (number) => {
    const value = String(number || '').trim().replace(/\s+/g, '');
    const ifOhMatch = value.match(/^IF\/OH(.+)$/i);

    if (ifOhMatch) {
      return [`IF${ifOhMatch[1]}`, `OH${ifOhMatch[1]}`];
    }

    return value ? [value] : [];
  };

  const getProductNumbers = (row) => {
    const numbers = [];

    Object.entries(row).forEach(([key, value]) => {
      if (!productFieldPattern.test(key) || !value || /call for info|in development/i.test(value)) return;
      const matches = String(value).match(partNumberPattern) || [];
      numbers.push(...matches.flatMap(expandProductNumber));
    });

    return unique(numbers);
  };

  const renderRows = (root, rows, config) => {
    const results = root.querySelector('[data-kit-results]');

    if (!rows.length) {
      renderEmpty(results, config.emptyText);
      return;
    }

    results.innerHTML = rows.map((row) => {
      const cpl = row['CPL #'] || '';
      const model = row.MODEL || row.Model || '';
      const title = [row['Engine Series'], cpl, model].filter(Boolean).join(' - ');
      const details = Object.entries(row)
        .filter(([key, value]) => !metadataKeys.has(key) && value && !/^call for info$/i.test(value))
        .map(([key, value]) => `
          <div>
            <dt>${escapeHtml(key)}</dt>
            <dd>${escapeHtml(value)}</dd>
          </div>
        `)
        .join('');
      const products = getProductNumbers(row).map((number) => {
        const url = buildProductUrl(number, config);
        return url ? `<a class="limitless-kit-selector__product" href="${url}">${escapeHtml(number)}</a>` : '';
      }).join('');

      return `
        <article class="limitless-kit-selector__card">
          <h3>${escapeHtml(title)}</h3>
          <dl class="limitless-kit-selector__details">${details}</dl>
          ${products ? `<div class="limitless-kit-selector__products">${products}</div>` : ''}
        </article>
      `;
    }).join('');
  };

  const showError = (results, error) => {
    console.error('[LimitlessKitSelector]', error);
    renderEmpty(results, 'Selector data is temporarily unavailable. Please try again.');
  };

  const initRoot = (root) => {
    if (!root || root.dataset.initialized === 'true') return;

    const config = readConfig(root);
    const seriesSelect = root.querySelector('[data-kit-series]');
    const cplSelect = root.querySelector('[data-kit-cpl]');
    const results = root.querySelector('[data-kit-results]');

    if (!seriesSelect || !cplSelect || !results) return;

    root.dataset.initialized = 'true';
    setOptions(seriesSelect, [], config.seriesPlaceholder);
    setOptions(cplSelect, [], config.cplPlaceholder);
    cplSelect.disabled = true;
    renderEmpty(results, 'Loading kit data...');

    fetchJson(buildProxyUrl(config.appProxyBasePath, '/kit/options'))
      .then((payload) => {
        setOptions(seriesSelect, payload.series || [], config.seriesPlaceholder);
        renderEmpty(results, config.chooseSeriesText);
      })
      .catch((error) => showError(results, error));

    seriesSelect.addEventListener('change', () => {
      const selectedSeries = seriesSelect.value;
      setOptions(cplSelect, [], config.cplPlaceholder);
      cplSelect.disabled = true;

      if (!selectedSeries) {
        renderEmpty(results, config.chooseSeriesText);
        return;
      }

      renderEmpty(results, 'Loading CPL options...');

      fetchJson(buildProxyUrl(config.appProxyBasePath, '/kit/options', { series: selectedSeries }))
        .then((payload) => {
          if (seriesSelect.value !== selectedSeries) return;

          setOptions(cplSelect, payload.cpls || [], config.cplPlaceholder);
          cplSelect.disabled = false;
          renderEmpty(results, config.chooseText);
        })
        .catch((error) => showError(results, error));
    });

    cplSelect.addEventListener('change', () => {
      const selectedSeries = seriesSelect.value;
      const selectedCpl = cplSelect.value;

      if (!selectedSeries || !selectedCpl) {
        renderEmpty(results, selectedSeries ? config.chooseText : config.chooseSeriesText);
        return;
      }

      renderEmpty(results, 'Loading matching kits...');

      fetchJson(buildProxyUrl(config.appProxyBasePath, '/kit/results', {
        series: selectedSeries,
        cpl: selectedCpl
      }))
        .then((payload) => {
          if (seriesSelect.value !== selectedSeries || cplSelect.value !== selectedCpl) return;
          renderRows(root, payload.rows || [], config);
        })
        .catch((error) => showError(results, error));
    });
  };

  const initAll = (scope = document) => {
    scope.querySelectorAll(rootSelector).forEach(initRoot);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
